import { ClerkProvider } from '@clerk/clerk-react';
import { HeroUIProvider } from '@heroui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';

import { AuthProvider } from '@/lib/auth';
import { routeTree } from './routeTree.gen';
import './styles/globals.css';

// Import API client to initialize it
import '@/lib/api/client';

// Clerk publishable key (required)
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY in environment variables');
}

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: 1,
    },
  },
});

// Create router
const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

// Register router for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Clerk theme appearance - works with dark/light mode
const clerkAppearance = {
  baseTheme: undefined,
  variables: {
    colorPrimary: 'hsl(var(--relay-foreground))',
    colorBackground: 'hsl(var(--relay-content1))',
    colorInputBackground: 'hsl(var(--relay-content2))',
    colorInputText: 'hsl(var(--relay-foreground))',
    colorText: 'hsl(var(--relay-foreground))',
    colorTextSecondary: 'hsl(var(--relay-default-500))',
    colorDanger: '#ef4444',
    colorSuccess: '#22c55e',
    colorWarning: '#f59e0b',
    borderRadius: '0.5rem',
  },
  elements: {
    // Auth forms
    formButtonPrimary:
      'bg-foreground text-background hover:opacity-90 transition-opacity',
    card: 'bg-content1 border border-default-100 shadow-xl',
    headerTitle: 'text-foreground',
    headerSubtitle: 'text-default-500',
    socialButtonsBlockButton:
      'bg-content2 border-default-200 text-foreground hover:bg-content3',
    socialButtonsBlockButtonText: 'text-foreground',
    dividerLine: 'bg-default-200',
    dividerText: 'text-default-500',
    formFieldLabel: 'text-default-600',
    formFieldInput:
      'bg-content2 border-default-200 text-foreground placeholder:text-default-400',
    footerActionLink: 'text-foreground hover:opacity-80',
    identityPreview: 'bg-content2 border-default-200',
    identityPreviewText: 'text-foreground',
    identityPreviewEditButton: 'text-default-500 hover:text-foreground',
    formFieldInputShowPasswordButton: 'text-default-500 hover:text-foreground',
    otpCodeFieldInput: 'bg-content2 border-default-200 text-foreground',
    formResendCodeLink: 'text-foreground hover:opacity-80',
    alert: 'bg-content2 border-default-200',
    alertText: 'text-default-600',
    // User button popover
    userButtonPopoverCard: 'bg-content1 border border-default-100 shadow-lg',
    userButtonPopoverActionButton: 'text-foreground hover:bg-content2',
    userButtonPopoverActionButtonText: 'text-foreground',
    userButtonPopoverFooter: 'border-default-100',
    // Organization switcher - clean, no background
    organizationSwitcherTrigger:
      'bg-transparent border-0 text-foreground hover:bg-default-100 rounded-md px-2 py-1 transition-colors',
    organizationSwitcherTriggerIcon: 'text-default-400',
    organizationPreviewMainIdentifier: 'text-foreground text-sm font-medium',
    organizationPreviewSecondaryIdentifier: 'text-default-500',
    organizationSwitcherPopoverCard: 'bg-content1 border border-default-100 shadow-lg',
    organizationSwitcherPopoverActionButton: 'text-foreground hover:bg-content2',
    organizationSwitcherPopoverActionButtonText: 'text-foreground text-sm',
    organizationSwitcherPopoverActions: 'border-default-100',
    organizationPreviewAvatarContainer: 'hidden',
  },
};

// Render app
ReactDOM.createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      afterSignOutUrl="/sign-in"
      appearance={clerkAppearance}
    >
      <AuthProvider>
        <HeroUIProvider>
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
          </QueryClientProvider>
        </HeroUIProvider>
      </AuthProvider>
    </ClerkProvider>
  </StrictMode>
);
